import {
	RiArrowRightLine,
	RiBuilding2Line,
	RiCheckLine,
	RiSkipForwardLine,
	RiTeamLine,
	RiUserLine,
} from "@remixicon/react";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { toast } from "sonner";
import { fetchClient } from "@/api/client";
import { $api } from "@/api/query";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/_app/onboarding")({
	component: OnboardingPage,
});

const STEPS = [
	{
		key: "profile",
		label: "Set up your profile",
		description: "Add your name and avatar to personalize your account.",
		icon: RiUserLine,
	},
	{
		key: "organization",
		label: "Configure your organization",
		description: "Set your organization name, logo, and basic settings.",
		icon: RiBuilding2Line,
	},
	{
		key: "invite_team",
		label: "Invite your team",
		description: "Add team members to start collaborating.",
		icon: RiTeamLine,
	},
];

function OnboardingPage() {
	const router = useRouter();
	const queryClient = useQueryClient();

	const { data, isLoading } = $api.useQuery("get", "/api/v1/onboarding");

	const stepsCompleted = data?.stepsCompleted ?? [];
	const currentStep = data?.currentStep ?? "profile";
	const isComplete = data?.isComplete ?? false;

	async function handleCompleteStep(step: string) {
		const { error } = await fetchClient.POST("/api/v1/onboarding/complete-step", {
			body: { step },
		});
		if (error) {
			toast.error("Failed to complete step");
			return;
		}
		queryClient.invalidateQueries({ queryKey: ["get", "/api/v1/onboarding"] });
	}

	async function handleSkip() {
		const { error } = await fetchClient.POST("/api/v1/onboarding/skip");
		if (error) {
			toast.error("Failed to skip onboarding");
			return;
		}
		toast.success("Onboarding complete!");
		await router.navigate({ to: "/dashboard" });
	}

	if (isLoading) {
		return <p className="text-muted-foreground">Loading...</p>;
	}

	if (isComplete) {
		return (
			<div className="flex flex-col items-center justify-center py-16 text-center max-w-md mx-auto">
				<div className="size-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
					<RiCheckLine className="size-8 text-primary" />
				</div>
				<h1 className="text-2xl font-bold mb-2">All set!</h1>
				<p className="text-muted-foreground mb-6">
					You've completed all onboarding steps. You're ready to go.
				</p>
				<Button onClick={() => router.navigate({ to: "/dashboard" })}>
					Go to Dashboard
					<RiArrowRightLine className="size-4 ml-1" />
				</Button>
			</div>
		);
	}

	return (
		<div className="max-w-2xl mx-auto py-8 space-y-8">
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-2xl font-bold tracking-tight">Welcome! Let's get started</h1>
					<p className="text-muted-foreground mt-1">
						Complete these steps to set up your workspace.
					</p>
				</div>
				<Button variant="ghost" size="sm" onClick={handleSkip}>
					<RiSkipForwardLine className="size-4 mr-1" />
					Skip all
				</Button>
			</div>

			{/* Progress bar */}
			<div className="flex gap-2">
				{STEPS.map((step) => (
					<div
						key={step.key}
						className={`h-1.5 flex-1 rounded-full transition-colors ${
							stepsCompleted.includes(step.key)
								? "bg-primary"
								: step.key === currentStep
									? "bg-primary/40"
									: "bg-muted"
						}`}
					/>
				))}
			</div>

			{/* Step cards */}
			<div className="space-y-3">
				{STEPS.map((step, index) => {
					const completed = stepsCompleted.includes(step.key);
					const isCurrent = step.key === currentStep;

					return (
						<div
							key={step.key}
							className={`flex items-center gap-4 p-4 rounded-lg border transition-colors ${
								completed
									? "bg-muted/30 border-border"
									: isCurrent
										? "bg-background border-primary shadow-sm"
										: "bg-background border-border opacity-50"
							}`}
						>
							<div
								className={`size-10 rounded-full flex items-center justify-center shrink-0 ${
									completed
										? "bg-primary text-primary-foreground"
										: isCurrent
											? "bg-primary/10 text-primary"
											: "bg-muted text-muted-foreground"
								}`}
							>
								{completed ? <RiCheckLine className="size-5" /> : <step.icon className="size-5" />}
							</div>

							<div className="flex-1">
								<p
									className={`font-medium ${completed ? "line-through text-muted-foreground" : ""}`}
								>
									Step {index + 1}: {step.label}
								</p>
								<p className="text-sm text-muted-foreground">{step.description}</p>
							</div>

							{!completed && isCurrent && (
								<Button size="sm" onClick={() => handleCompleteStep(step.key)}>
									Complete
									<RiArrowRightLine className="size-4 ml-1" />
								</Button>
							)}

							{completed && <RiCheckLine className="size-5 text-primary shrink-0" />}
						</div>
					);
				})}
			</div>
		</div>
	);
}
